/**
 * DataTable organism — extends the DataTable molecule with selection, search, column visibility, and one shared toolbar.
 *
 * **Purpose:** Reusable table section with optional row selection, debounced global search, column visibility,
 * and slots — no data fetching (consumers pass `columns` / `data`).
 *
 * **Composition:** Checkbox atom, SearchInput, Button, DropdownMenu organism, DataTable molecule.
 *
 * **Contract with the molecule:** The molecule owns `useReactTable`, table DOM, and pagination. Toolbar UI is
 * wired through `renderToolbar(table)` so controls use the same `table` instance as the grid.
 *
 * **Lineage:** WorkspaceProfilesList, DataTableShowcasePage (and any route using `DataTableOrganism`).
 */
import { useState } from "react";
import type { ColumnDef, Table } from "@tanstack/react-table";
import { Check, Columns3 } from "lucide-react";
import { Button, SearchInput } from "@/components/ui";
import { Checkbox } from "../../atoms/Checkbox";
import { DataTable } from "../../molecules/DataTable";
import { dataTableColumnLabel } from "../../molecules/DataTable";
import { DropdownMenu } from "../DropdownMenu";
import type {
  DataTableOrganismColumnVisibilityMenuProps,
  DataTableOrganismProps,
} from "./DataTable.types";
import { organismSelectionColumnLayout } from "./DataTable.types";
import {
  dataTableOrganismColumnMenuContentClass,
  dataTableOrganismColumnVisibilityTriggerClass,
  dataTableOrganismSelectCellWrapperClass,
  dataTableOrganismToolbarControlsClass,
  dataTableOrganismToolbarEndSlotClass,
  dataTableOrganismToolbarTitleClass,
  getDataTableOrganismToolbarClassName,
} from "./DataTable.styles";
import {
  resolveEnableRowSelection,
  resolveOrganismSearchKeys,
  rowSelectionToSelectedIds,
  selectedIdsToRowSelection,
} from "./DataTable.utils";

function withSelectionColumn<TData extends { id: string }>(
  columns: ColumnDef<TData>[],
  enableSelection: boolean,
  selectionDisabled: boolean
): ColumnDef<TData>[] {
  if (!enableSelection) return columns;

  const hasSelect = columns.some((c) => c.id === "select");
  if (hasSelect) return columns;

  return [
    {
      id: "select",
      ...organismSelectionColumnLayout,
      header: ({ table }) => {
        const hasSelectable = table
          .getRowModel()
          .rows.some((row) => row.getCanSelect());
        return (
          <div className={dataTableOrganismSelectCellWrapperClass}>
            {hasSelectable ? (
              <Checkbox
                size="sm"
                checked={table.getIsAllPageRowsSelected()}
                indeterminate={
                  !table.getIsAllPageRowsSelected() &&
                  table.getIsSomePageRowsSelected()
                }
                disabled={selectionDisabled}
                onChange={(event) =>
                  table.toggleAllPageRowsSelected(!!event.target.checked)
                }
                aria-label="Select all rows"
              />
            ) : null}
          </div>
        );
      },
      cell: ({ row }) => (
        // Stop propagation so toggling the row checkbox never triggers the
        // row's onRowClick (e.g. navigating to a detail page).
        <div
          className={dataTableOrganismSelectCellWrapperClass}
          onClick={(event) => event.stopPropagation()}
        >
          {row.getCanSelect() ? (
            <Checkbox
              size="sm"
              checked={row.getIsSelected()}
              disabled={selectionDisabled}
              onChange={(event) => row.toggleSelected(!!event.target.checked)}
              aria-label="Select row"
            />
          ) : null}
        </div>
      ),
      enableSorting: false,
      enableHiding: false,
    } as ColumnDef<TData>,
    ...columns,
  ];
}

function ColumnVisibilityMenu<TData extends { id: string }>({
  table,
  columnVisibilityLabel,
}: DataTableOrganismColumnVisibilityMenuProps<TData>) {
  return (
    <DropdownMenu>
      <DropdownMenu.Trigger asChild>
        <Button
          type="icon"
          intent="muted"
          size="md"
          title={columnVisibilityLabel}
          aria-label={columnVisibilityLabel}
          icon={<Columns3 />}
          className={dataTableOrganismColumnVisibilityTriggerClass}
        />
      </DropdownMenu.Trigger>
      <DropdownMenu.Content align="end" className={dataTableOrganismColumnMenuContentClass}>
        <DropdownMenu.Label>Columns</DropdownMenu.Label>
        <DropdownMenu.Separator />
        <DropdownMenu.Group>
          {table
            .getAllColumns()
            .filter(
              (column) =>
                typeof column.accessorFn !== "undefined" && column.getCanHide()
            )
            .map((column) => {
              const visible = column.getIsVisible();
              const label = dataTableColumnLabel(column);
              return (
                <DropdownMenu.Item
                  key={column.id}
                  className="gap-2"
                  onClick={() => column.toggleVisibility(!visible)}
                >
                  {visible ? <Check className="h-4 w-4 shrink-0" aria-hidden /> : (
                    <span className="inline-flex w-4 shrink-0" aria-hidden />
                  )}
                  <span className="text-sm text-dark-invirtus/80">{label}</span>
                </DropdownMenu.Item>
              );
            })}
        </DropdownMenu.Group>
      </DropdownMenu.Content>
    </DropdownMenu>
  );
}

export function DataTableOrganism<TData extends { id: string }>({
  columns,
  data,
  pageSize = 10,
  enableSelection = true,
  enableRowSelection,
  selectedIds,
  onSelectedIdsChange,
  selectionDisabled = false,
  prependToolbarControls,
  enableSearch = true,
  searchKeys: searchKeysProp,
  searchPlaceholder = "Search…",
  enableColumnVisibility = true,
  columnVisibilityLabel = "Column visibility",
  dataTableProps,
  columnFilters,
  onColumnFiltersChange,
  fillHeight = false,
  toolbarTitle,
  toolbarEndSlot,
  paginationFooterSlot,
  paginationItemLabel = "rows",
}: DataTableOrganismProps<TData>) {
  const enhancedColumns = withSelectionColumn(
    columns,
    enableSelection,
    selectionDisabled
  );
  const resolvedSearchKeys = resolveOrganismSearchKeys(searchKeysProp);
  const isSelectionControlled = selectedIds !== undefined;
  const rowSelection = isSelectionControlled
    ? selectedIdsToRowSelection(selectedIds)
    : undefined;

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const searchControl = enableSearch ? (
    <SearchInput
      value={search}
      onChange={setSearch}
      debounceDelay={300}
      onDebouncedChange={setDebouncedSearch}
      placeholder={searchPlaceholder}
      showClearButton
    />
  ) : null;

  const toolbarControls = (table: Table<TData>) => (
    <div className={dataTableOrganismToolbarControlsClass}>
      {prependToolbarControls}
      {searchControl}
      {enableColumnVisibility ? (
        <ColumnVisibilityMenu table={table} columnVisibilityLabel={columnVisibilityLabel} />
      ) : null}
      {toolbarEndSlot ? (
        <div className={dataTableOrganismToolbarEndSlotClass}>{toolbarEndSlot}</div>
      ) : null}
    </div>
  );

  const organismToolbar = (table: Table<TData>) => (
    <div className={getDataTableOrganismToolbarClassName(!!toolbarTitle)}>
      {toolbarTitle ? (
        <div className={dataTableOrganismToolbarTitleClass}>{toolbarTitle}</div>
      ) : null}
      {toolbarControls(table)}
    </div>
  );

  // Suppress the toolbar row entirely when it would be empty (no title, search,
  // column visibility, end slot, or prepended controls) so it adds no dead space.
  const hasToolbarContent =
    !!toolbarTitle ||
    enableSearch ||
    enableColumnVisibility ||
    !!toolbarEndSlot ||
    !!prependToolbarControls;

  const searchTableProps =
    enableSearch ?
      {
        globalFilter: debouncedSearch,
        onGlobalFilterChange: (next: string) => {
          setDebouncedSearch(next);
          setSearch(next);
        },
        globalSearchKeys: resolvedSearchKeys,
      }
      : {};

  const { className: dataTableClassName, ...restDataTableProps } = dataTableProps ?? {};

  return (
    <DataTable<TData>
      {...restDataTableProps}
      columns={enhancedColumns}
      data={data}
      pagination
      pageSize={pageSize}
      paginationItemLabel={paginationItemLabel}
      footerSlot={paginationFooterSlot}
      columnFilters={columnFilters}
      onColumnFiltersChange={onColumnFiltersChange}
      fillHeight={fillHeight}
      enableRowSelection={resolveEnableRowSelection(enableRowSelection)}
      getRowId={(row) => row.id}
      rowSelection={rowSelection}
      onRowSelectionChange={
        isSelectionControlled || onSelectedIdsChange
          ? (next) => onSelectedIdsChange?.(rowSelectionToSelectedIds(next))
          : undefined
      }
      {...searchTableProps}
      className={dataTableClassName}
      renderToolbar={hasToolbarContent ? organismToolbar : undefined}
    />
  );
}
